import { Suspense, useEffect, useMemo, useRef } from "react"
import { useAnimations, useGLTF } from "@react-three/drei"
// three v0.170 expone `clone` como named export — antes era
// `SkeletonUtils.clone`. El TS de Vercel falla con el import de
// objeto; usar la función directa.
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js"
import * as THREE from "three"

import type { AvatarMeshDef } from "./avatarMeshes"

// Sprint 5 v2 (2026-05-05) — Mesh rigged con animaciones.
//
// Reescrito para soportar `animationKey` que el componente Avatar
// alimenta según la actividad del Tripulante:
//   · "idle"  — quieto (animación principal del .glb)
//   · "walk"  — caminando hacia un destino
//   · "run"   — corriendo (Space + click en desktop)
//   · "wave"  — saludo (one-shot, S en desktop)
//
// El .glb de Cristal (alfa y omega) trae su propio set; el resolver
// hace match por nombre case-insensitive ("idle" ⊂ "Idle"/"Armature|Idle"/
// "mixamo.com|Idle", "walk" ⊂ "Walking", etc.).
//
// Crossfade entre clips para que la transición sea fluida — la cabeza
// no salta ni el avatar se "rebota" al cambiar de estado. La wave es
// one-shot: se dispara una vez y vuelve a la animación base cuando
// termina (gestionado por el parent con un timer).
//
// Cada Avatar instancia clona la escena con SkeletonUtils para que dos
// peers con la misma firma no compartan estado de huesos (sin clonar,
// si dos cristales caminan se mueven idénticos).

export type AvatarAnimKey = "idle" | "walk" | "run" | "wave" | "jump"

interface Props {
    def: AvatarMeshDef
    /** Color que se aplica como emissive a los materiales del .glb si
     *  no traen uno propio. Permite que la malla brille con la firma. */
    emissiveColor: string
    emissiveIntensity: number
    /** 0..1 — fade-in durante la fase materializing. */
    materializeOpacity: number
    /** Animación a reproducir. Default "idle". */
    animationKey?: AvatarAnimKey
}

const FADE_SEC = 0.28

// Resolver de nombre de clip → animationKey canónica.
//
// Soporta nombres en español (clips de Meshy: "Inactivo 11", "Caminando",
// "Corriendo", "Gran Ola Hola", "Agita una Mano") y en inglés (Mixamo:
// "Idle", "Walking", "Running", "Wave"). Patrón: matchear substring
// case-insensitive.
//
// Orden importa: wave/run se chequean ANTES que walk/idle porque
// "Corriendo" no contiene "walk" ni "idle" pero "Inactivo" no contiene
// "walk" — son disjuntos. Wave primero por si un clip tuviera
// "Saludando corriendo" o similar (no esperado).
function resolveClipKey(clipName: string): AvatarAnimKey | null {
    const n = clipName.toLowerCase()
    if (
        n.includes("wave") ||
        n.includes("salud") ||
        n.includes("greet") ||
        n.includes("agita") ||
        n.includes("ola hola") ||
        n.includes("gran ola")
    ) {
        return "wave"
    }
    if (
        n.includes("run") ||
        n.includes("sprint") ||
        n.includes("corriend") ||
        n.includes("correr")
    ) {
        return "run"
    }
    // 2026-05-05 v6 — Salto. Mixamo nombra el clip "Jump" / "Jumping"
    // o variantes en español ("Saltando", "Salto con brazos abiertos").
    // Lo ponemos antes de "walk" porque "saltar" no contiene "walk"
    // ni "idle" — son disjuntos pero el orden ayuda a la lectura.
    if (
        n.includes("jump") ||
        n.includes("salto") ||
        n.includes("saltand") ||
        n.includes("saltar")
    ) {
        return "jump"
    }
    if (n.includes("walk") || n.includes("camina")) {
        return "walk"
    }
    if (
        n.includes("inactiv") ||
        n.includes("idle") ||
        n.includes("stand") ||
        n.includes("reposo") ||
        n.includes("quiet")
    ) {
        return "idle"
    }
    return null
}

// Multiplicadores de velocidad de animación. La animación "Corriendo"
// del GLB de Cristal corre a un ritmo natural pero el avatar avanza
// más rápido que las pisadas (sensación de deslizar). Acelerar la
// animación 1.3x cierra ese gap visualmente sin tocar el GLB.
// 2026-05-05 v2 — Bajado de 1.4 a 1.3 porque ahora el LocalAvatar
// avanza a velocidad CONSTANTE sin pico inicial del damp3 viejo; la
// animación necesita menos aceleración.
// 2026-05-05 v3 — RUN_SPEED del LocalAvatar subió de 3.6 a 4.5 u/s
// para alinear el desplazamiento con el clip Run x1.3. Mantenemos
// timeScale 1.3 — el bump al RUN_SPEED resuelve el desalineo sin
// tocar el clip.
const ANIM_TIME_SCALE: Partial<Record<AvatarAnimKey, number>> = {
    run: 1.3,
}

// 2026-05-06 — Fallback visual mientras la malla GLB todavía no
// terminó de descargar/parsear. Antes era `null`: el Tripulante veía
// vacío hasta que la malla aparecía sin transición. Ahora durante la
// carga aparece un orbe luminoso del color de la firma flotando en
// el lugar del cuerpo, así el campo nunca se ve "muerto".
function MaterializingOrb({
    color,
    intensity,
}: {
    color: string
    intensity: number
}) {
    return (
        <group position={[0, 1, 0]}>
            <mesh>
                <sphereGeometry args={[0.5, 32, 32]} />
                <meshBasicMaterial
                    color={color}
                    transparent
                    opacity={0.95}
                    depthWrite={false}
                />
            </mesh>
            <mesh>
                <sphereGeometry args={[0.85, 24, 24]} />
                <meshBasicMaterial
                    color={color}
                    transparent
                    opacity={0.25}
                    depthWrite={false}
                />
            </mesh>
            <pointLight
                color={color}
                intensity={intensity * 1.5}
                distance={6}
                decay={1.6}
            />
        </group>
    )
}

export function MaterializedMesh(props: Props) {
    return (
        <Suspense
            fallback={
                <MaterializingOrb
                    color={props.emissiveColor}
                    intensity={props.emissiveIntensity}
                />
            }
        >
            <MaterializedMeshInner {...props} />
        </Suspense>
    )
}

function MaterializedMeshInner({
    def,
    materializeOpacity,
    animationKey = "idle",
}: Props) {
    const gltf = useGLTF(def.url)

    // cloneSkeleton (alias de SkeletonUtils.clone) preserva la
    // jerarquía de huesos para SkinnedMesh. Sin esto, varios avatares
    // compartirían skeleton y se animarían igual.
    const scene = useMemo(
        () => cloneSkeleton(gltf.scene) as THREE.Object3D,
        [gltf.scene]
    )

    const { actions } = useAnimations(gltf.animations, scene)

    // Indexar acciones por key canónica. Si el .glb tiene varios clips
    // que matchean (raro), el primero gana.
    //
    // 2026-05-06 — Diagnóstico de mapeo: imprime UNA línea por avatar
    // listando los clips reales del GLB y a qué key canónica resuelve
    // cada uno. Permite ver desde DevTools si el resolver está
    // matcheando los nombres como esperamos. El log lo dispara solo
    // el primer mount (no en cada re-render del HMR).
    const animMap = useMemo(() => {
        const m: Partial<Record<AvatarAnimKey, THREE.AnimationAction>> = {}
        const summary: Array<string> = []
        for (const [name, action] of Object.entries(actions)) {
            if (!action) continue
            const key = resolveClipKey(name)
            if (key && !m[key]) m[key] = action
            summary.push(`${name}→${key ?? "—"}`)
        }
        if (summary.length > 0 && typeof console !== "undefined") {
            console.log(`[domo] anim clips: ${summary.join(" | ")}`)
        }
        return m
    }, [actions])

    // Configurar wave + jump como one-shot que clampan al final +
    // aplicar timeScale por key (run acelerado 1.3x para que las
    // pisadas matcheen el avance). 2026-05-05 v6 — el salto también
    // es one-shot: empieza al picar S y termina cuando el clip se
    // completa (~1.1s). LocalAvatar gestiona la transición de
    // animKey de vuelta a walk/run/idle; acá solo nos aseguramos
    // que el clip no quede en loop infinito.
    useEffect(() => {
        const wave = animMap.wave
        if (wave) {
            wave.setLoop(THREE.LoopOnce, 1)
            wave.clampWhenFinished = true
        }
        const jump = animMap.jump
        if (jump) {
            jump.setLoop(THREE.LoopOnce, 1)
            jump.clampWhenFinished = true
        }
        for (const [key, action] of Object.entries(animMap)) {
            if (!action) continue
            const scale = ANIM_TIME_SCALE[key as AvatarAnimKey] ?? 1
            action.timeScale = scale
        }
    }, [animMap])

    // Crossfade entre acciones cuando animationKey cambia.
    const currentRef = useRef<AvatarAnimKey | null>(null)
    useEffect(() => {
        let nextKey: AvatarAnimKey = animationKey
        let next = animMap[nextKey]
        if (!next) {
            // Fallback en cadena: la key pedida no existe → idle → cualquier
            // disponible. Si el .glb no tiene NINGUNA animación, no hace nada.
            next = animMap.idle
            nextKey = "idle"
        }
        if (!next) {
            // Sin animaciones disponibles — el mesh queda quieto.
            return
        }
        if (currentRef.current === nextKey) return
        const prev = currentRef.current ? animMap[currentRef.current] : null
        if (prev && prev !== next) {
            prev.fadeOut(FADE_SEC)
        }
        next.reset().fadeIn(FADE_SEC).play()
        currentRef.current = nextKey
    }, [animationKey, animMap])

    // Aplicar SOLO opacity al material clonado.
    //
    // 2026-05-06 — Removida la inyección de emissive con la paleta del
    // modelo. La GLB de Meshy trae sus propios mapas de albedo + emissive
    // (textura del rigging) y pisarlos con `std.emissive = paletteColor`
    // multiplicaba ese tinte por encima de los píxeles de la textura
    // original, dándole al avatar un tono raro (saturado de azul/cyan
    // para Cristal, naranja para Plasma, magenta para Biopolímero).
    // Ahora dejamos el material del GLB intacto: los avatares se ven
    // tal como Meshy los exportó. La paleta del modelo sigue
    // alimentando los HALOS, anillos rotatorios y sparkles que viven
    // FUERA del cuerpo (en Avatar.tsx), así la firma del Tripulante
    // se reconoce por el aura, no por un tinte sobre la piel.
    // Mantenemos la modulación de `opacity` para el fade-in durante
    // la fase materializing — es independiente del color y necesario
    // para el rito visual del descenso.
    useEffect(() => {
        const op = Math.max(0, Math.min(1, materializeOpacity))
        scene.traverse((obj: THREE.Object3D) => {
            const mesh = obj as THREE.Mesh
            if (!mesh.isMesh) return
            const mats = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material]
            for (const m of mats) {
                const std = m as THREE.MeshStandardMaterial
                if ("transparent" in std) {
                    std.transparent = op < 1
                    std.opacity = op
                    std.depthWrite = op > 0.5
                    std.needsUpdate = true
                }
            }
        })
    }, [scene, materializeOpacity])

    return (
        <primitive
            object={scene}
            scale={def.scale}
            position={[0, def.offsetY, 0]}
            rotation={[0, def.rotationY ?? 0, 0]}
        />
    )
}
